import React, { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

export default function SignaturePad({ onChange, height = 180 }) {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasInk, setHasInk] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ratio = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        const ctx = canvas.getContext("2d");
        ctx.scale(ratio, ratio);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#0A0A0A";
        ctx.lineWidth = 2.4;
    }, []);

    const getPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const isTouch = e.touches && e.touches[0];
        const clientX = isTouch ? e.touches[0].clientX : e.clientX;
        const clientY = isTouch ? e.touches[0].clientY : e.clientY;
        return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const start = (e) => {
        e.preventDefault();
        const { x, y } = getPos(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
    };

    const move = (e) => {
        if (!isDrawing) return;
        e.preventDefault();
        const { x, y } = getPos(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.lineTo(x, y);
        ctx.stroke();
        setHasInk(true);
    };

    const end = () => {
        if (!isDrawing) return;
        setIsDrawing(false);
        if (onChange) {
            onChange(canvasRef.current.toDataURL("image/png"));
        }
    };

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasInk(false);
        if (onChange) onChange("");
    };

    return (
        <div data-testid="signature-pad-wrap">
            <div className="relative bg-white border-2 border-black/15 hover:border-[#FF6B1A] transition-colors" style={{ height }}>
                <canvas
                    ref={canvasRef}
                    onMouseDown={start}
                    onMouseMove={move}
                    onMouseUp={end}
                    onMouseLeave={end}
                    onTouchStart={start}
                    onTouchMove={move}
                    onTouchEnd={end}
                    className="w-full h-full cursor-crosshair touch-none"
                    style={{ display: "block" }}
                    data-testid="signature-canvas"
                />
                {!hasInk && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-black/30 text-sm">
                        Sign here
                    </div>
                )}
                <div className="absolute bottom-2 left-3 right-3 border-t border-black/20 pointer-events-none" />
                <div className="absolute bottom-3 left-3 text-[9px] uppercase tracking-[0.18em] text-black/35 pointer-events-none">×</div>
            </div>
            <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-black/55">Use mouse, trackpad, or finger to sign</span>
                <button type="button" onClick={clear} className="text-xs font-semibold text-black/65 hover:text-[#FF6B1A] inline-flex items-center gap-1.5" data-testid="signature-clear">
                    <Eraser size={13} /> Clear
                </button>
            </div>
        </div>
    );
}
